import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

const SAVINGS_BADGE_CLASS = 'price-savings-badge';

/**
 * @typedef {Object} MoneyFormat
 * @property {number} value - The numeric value
 * @property {string} prefix - Anything before the number, e.g. 'A$'
 * @property {string} suffix - Anything after the number, e.g. ' AUD'
 * @property {number} decimals - Number of decimal places
 * @property {string} decimalSep - Decimal separator
 * @property {string} groupSep - Thousands separator, empty when absent
 */

/**
 * Parses a rendered money string, preserving its formatting so we can mirror it.
 * Handles '$32.00', 'A$27.20', '€1.234,56', '1 234,56 kr'.
 * @param {string | null | undefined} text
 * @returns {MoneyFormat | null}
 */
function parseMoney(text) {
  if (!text) return null;
  const raw = text.trim();
  const match = raw.match(/\d[\d.,\s ']*\d|\d/);
  if (!match || match.index == null) return null;

  const numeric = match[0];
  const prefix = raw.slice(0, match.index);
  const suffix = raw.slice(match.index + numeric.length);

  // A trailing separator followed by 1-2 digits is a decimal, not a thousands group.
  const decimal = numeric.match(/([.,])(\d{1,2})$/);
  const decimals = decimal ? decimal[2].length : 0;
  const decimalSep = decimal ? decimal[1] : '.';

  const integerText = decimal ? numeric.slice(0, -(decimals + 1)) : numeric;
  const group = integerText.match(/[.,\s ']/);
  const value = Number(`${integerText.replace(/[.,\s ']/g, '')}.${decimal ? decimal[2] : '0'}`);
  if (!Number.isFinite(value)) return null;

  return { value, prefix, suffix, decimals, decimalSep, groupSep: group ? group[0] : '' };
}

/**
 * Drops currency-code letters from a captured format, so the badge reads 'Save $4.80'
 * rather than 'Save A$4.80'. The symbol is kept; only the locale/ISO letters go.
 * @param {MoneyFormat} format
 * @returns {MoneyFormat}
 */
function withoutCurrencyCode(format) {
  return {
    ...format,
    prefix: format.prefix.replace(/[A-Za-z]/g, '').trim(),
    suffix: format.suffix.replace(/[A-Za-z]/g, '').trim(),
  };
}

/**
 * Renders a value using the formatting captured from another money string.
 * @param {number} value
 * @param {MoneyFormat} format
 * @returns {string}
 */
function formatMoney(value, format) {
  const [integer, fraction] = value.toFixed(format.decimals).split('.');
  const grouped = format.groupSep ? integer.replace(/\B(?=(\d{3})+(?!\d))/g, format.groupSep) : integer;
  const number = fraction ? `${grouped}${format.decimalSep}${fraction}` : grouped;
  return `${format.prefix}${number}${format.suffix}`;
}

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {MutationObserver | null} */
  #savingsObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#startSavingsSync();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#savingsObserver?.disconnect();
    this.#savingsObserver = null;
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Keeps the savings badge in step with whatever prices are currently displayed.
   *
   * The badge is rendered server-side when the variant has a real compare-at price, but
   * subscription widgets and similar apps rewrite the price DOM in the browser, after Liquid
   * has run. Recomputing from the rendered values covers both cases with one code path.
   */
  #startSavingsSync() {
    if (this.dataset.showSavings !== 'true') return;
    this.#savingsObserver = new MutationObserver(this.#syncSavingsBadge);
    this.#syncSavingsBadge();
  }

  #observeSavings() {
    this.#savingsObserver?.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributeFilter: ['style', 'class'],
    });
  }

  /**
   * Folds an app's inline display toggle into the theme's own price__hidden class.
   * Inline styles outrank the stylesheet, which would otherwise stop the badge's layout
   * rules from applying, and leave the markup contradicting itself (price__hidden sitting
   * on an element inlined to display: block).
   * @param {HTMLElement | null} group
   * @returns {boolean} whether the group is visible
   */
  #normalizeVisibility(group) {
    if (!group) return false;
    const inline = group.style.display;
    const visible = inline ? inline !== 'none' : !group.classList.contains('price__hidden');
    if (inline) {
      group.classList.toggle('price__hidden', !visible);
      group.style.removeProperty('display');
    }
    return visible;
  }

  #syncSavingsBadge = () => {
    const saleGroup = /** @type {HTMLElement | null} */ (this.querySelector('.price__sale'));
    if (!saleGroup) return;
    const regularGroup = /** @type {HTMLElement | null} */ (this.querySelector('.price__regular'));

    // Detach before any writes, so our own mutations can't re-trigger the observer.
    this.#savingsObserver?.disconnect();

    const onSale = this.#normalizeVisibility(saleGroup);
    this.#normalizeVisibility(regularGroup);

    let label = null;
    if (onSale) {
      const sale = parseMoney(saleGroup.querySelector('.price-item--sale')?.textContent);
      const compare = parseMoney(saleGroup.querySelector('.compare-at-price')?.textContent);

      if (sale && compare && compare.value > sale.value) {
        const savings = compare.value - sale.value;
        label =
          this.dataset.savingsType === 'percentage'
            ? `Save ${Math.round((savings / compare.value) * 100)}%`
            : `Save ${formatMoney(savings, withoutCurrencyCode(compare))}`;
      }
    }

    const badge = saleGroup.querySelector(`.${SAVINGS_BADGE_CLASS}`);
    if (!label) {
      badge?.remove();
    } else if (badge) {
      if (badge.textContent !== label) badge.textContent = label;
    } else {
      const element = document.createElement('span');
      element.className = SAVINGS_BADGE_CLASS;
      element.textContent = label;
      saleGroup.appendChild(element);
    }

    this.#observeSavings();
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
